import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { ExportSchema, TableExport } from '../export/schemas'
import { TableExportQuery } from '../export/schemas'

/** Submit a snapshot request or read an existing export; never download workbook bytes into JS. */
export async function tableExportRequest(tableId: string, request: TableExportQuery | string, signal?: AbortSignal): Promise<TableExport> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to download tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/exports${typeof request === 'string' ? `/${request}` : ''}`
  const response = await fetch(url, {
    method: typeof request === 'string' ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body:
      typeof request === 'string'
        ? undefined
        : JSON.stringify({
            format: request.format,
            include_footnotes: request.include_footnotes,
            filters: request.filters,
            sorts: request.sorts,
            filter_match: request.filter_match,
            search: request.search,
            search_column_ids: request.search_column_ids,
            search_filenames: request.search_filenames,
            column_ids: request.column_ids,
            pinned_column_ids: request.pinned_column_ids,
            entry_column_index: request.entry_column_index,
            entry_column_pinned: request.entry_column_pinned,
          }),
    signal,
  })
  const { data, parseError } = await readResponseJson(response)
  signal?.throwIfAborted()
  if (!response.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    if (response.status >= 500) Sentry.captureException(new Error('Table export request failed'), { extra: getResponseDebugContext(response, url) })
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' ? detail : 'Unable to prepare this download.',
      response.status < 500 ? detail : undefined
    )
  }
  const parsed = ExportSchema.safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid table export response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The download response could not be read. Please retry.')
  }
  return parsed.data
}
